import {
  CSRF_HEADER,
  changeKindOf,
  isFolderOp,
  type ProjectDetail,
  type WikiChangeSet,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Users } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "../api.ts";
import { ApiError, ok } from "../api-errors.ts";
import { canChange } from "../projects/capabilities.ts";
import { useSession } from "../session.tsx";
import { peopleAudience } from "../trust/audience.ts";
import { folderPathText, itemName } from "./ChangeSetReview.tsx";
import { useChangeSet, wikiKeys } from "./queries.ts";
import { itemSize } from "./sizes.ts";

/**
 * The author's compact publish confirmation (spec `project-wiki` "Publish confirmation per
 * page", mock S4): what the working set will publish and who will read each page, with no
 * "Include pending" choice (it is the whole set) and no declassification (the full publish
 * dialog of the review keeps it). One call publishes every pending item; a `409` keeps the sheet
 * open, names the page that went stale and refreshes the set.
 */
export function AuthorPublishSheet(props: {
  open: boolean;
  onClose: () => void;
  project: ProjectDetail;
  changeSet: WikiChangeSet;
  /**
   * Opens the full publish dialog, where a manager may declassify a page (Q3): offered when a
   * page could be published wider and the viewer may declassify.
   */
  onDeclassify?: () => void;
}) {
  const { t } = useTranslation();
  const live = props.changeSet.items.filter(
    (i) => i.status === "pending" || i.status === "approved",
  );
  return (
    <AppDialog
      open={props.open}
      onClose={props.onClose}
      title={t("wiki.author.publishSheet.title", { count: live.length })}
      description={t("wiki.author.publishSheet.explain")}
    >
      <PublishList {...props} />
    </AppDialog>
  );
}

function PublishList(props: {
  onClose: () => void;
  project: ProjectDetail;
  changeSet: WikiChangeSet;
  onDeclassify?: (() => void) | undefined;
}) {
  const { project, onClose } = props;
  const { t } = useTranslation();
  const { csrfToken, userId } = useSession();
  const queryClient = useQueryClient();
  // The set as it is now (a 409 refreshes it: the stale page is then named).
  const changeSet = useChangeSet(project.id, props.changeSet.id).data ?? props.changeSet;
  const [conflict, setConflict] = useState<string | null>(null);
  const endpoint = api.api.projects[":projectId"].wiki["change-sets"][":id"];
  const param = { projectId: project.id, id: changeSet.id };
  const headers = { [CSRF_HEADER]: csrfToken };
  const preview = useQuery({
    queryKey: [...wikiKeys.all(project.id), "preview", changeSet.id, true],
    gcTime: 0,
    retry: false,
    queryFn: async () =>
      ok(await endpoint.preview.$post({ param, json: { includePending: true } }, { headers })),
  });
  const publish = useMutation({
    mutationFn: async () =>
      ok(await endpoint.publish.$post({ param, json: { includePending: true } }, { headers })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: wikiKeys.all(project.id) });
      onClose();
    },
    onError: async (error) => {
      await queryClient.invalidateQueries({ queryKey: wikiKeys.all(project.id) });
      setConflict(error instanceof ApiError && error.status === 409 ? error.message : null);
    },
  });
  const chosen = changeSet.items.filter((i) => i.status === "pending" || i.status === "approved");
  const stale = changeSet.items.filter((i) => i.status === "stale");
  // Publish only once the audience was shown for every page it publishes (as the full dialog).
  const shown =
    preview.isSuccess && chosen.every((item) => preview.data.items.some((p) => p.id === item.id));
  const valid = chosen.length > 0 && stale.length === 0 && shown;
  const folder = (path: readonly string[] | null | undefined) =>
    path?.length ? folderPathText(path) : t("wiki.changes.root");
  return (
    <form
      data-esi-content="wiki"
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) {
          publish.mutate();
        }
      }}
    >
      {preview.isPending ? (
        <Skeleton className="h-28" />
      ) : preview.isError ? (
        <Notice tone="danger">{preview.error.message}</Notice>
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line">
          {chosen.map((item) => {
            const readers = preview.data.items.find((p) => p.id === item.id)?.preview?.readers;
            const kind = changeKindOf(item.op);
            const size = itemSize(item);
            const facts = [
              t(`wiki.author.kind.${kind}`) + (size ? ` ${size}` : ""),
              ...(item.op === "create" ? [folder(item.folderPath)] : []),
              ...(item.op === "move" || isFolderOp(item.op)
                ? [t("wiki.author.publishSheet.unchanged")]
                : []),
            ];
            return (
              <li key={item.id} className="flex items-start justify-between gap-3 px-3 py-2.5">
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="break-words text-sm font-medium text-fg">{itemName(item)}</span>
                  <span className="text-xs text-fg-muted tabular-nums">{facts.join(" · ")}</span>
                </span>
                <span className="flex shrink-0 items-center gap-1 text-xs text-fg-secondary">
                  <Users aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
                  {readers
                    ? peopleAudience(
                        readers.length,
                        readers.length === 1 && readers[0]?.userId === userId,
                      )
                    : t("wiki.author.publishSheet.sameReaders")}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <p className="text-xs text-fg-muted">{t("wiki.author.publishSheet.footnote")}</p>
      {props.onDeclassify &&
      canChange(project, "declassify") &&
      preview.data?.items.some((p) => p.preview?.declassification.available) ? (
        <p className="text-xs text-fg-secondary">
          {t("wiki.author.publishSheet.declassifyHint")}{" "}
          <Button
            type="button"
            variant="link"
            size="xs"
            className="h-auto p-0 text-accent-text underline underline-offset-4"
            onClick={props.onDeclassify}
          >
            {t("wiki.author.publishSheet.declassify")}
          </Button>
        </p>
      ) : null}
      {publish.isError ? (
        <Notice tone="danger">
          {conflict && stale.length
            ? t("wiki.author.publishSheet.stale", {
                pages: stale.map((i) => itemName(i)).join(", "),
              })
            : publish.error.message}
        </Notice>
      ) : null}
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          {t("wiki.publish.cancel")}
        </Button>
        <Button type="submit" disabled={!valid || publish.isPending}>
          {t(publish.isPending ? "wiki.publish.publishing" : "wiki.author.publish")}
        </Button>
      </DialogFooter>
    </form>
  );
}
